// Real products for the shopping list: for each item, a good-value one and a
// premium one, with a link to a UK shop. Prices were checked in October 2026;
// they move, so the app shows them as a guide.

import type { CatalogueItem } from './types.ts';

export const PRODUCTS: Record<string, NonNullable<CatalogueItem['options']>> = {
  crate: {
    value: {
      name: "KONG Ultra-Strong Two Door Crate Secure Folding Dog Crate Medium/Large (92.7 cm)",
      shop: "Pets at Home",
      price: "£54",
      link: "https://www.petsathome.com/product/kong-two-door-ultra-strong-dog-crate-black-medium-large/7131677P",
      why: "36-inch size with a divider panel and two doors; folds flat and is well rated by owners.",
    },
    premium: {
      name: "Ferplast Superior 90 Dog Crate Medium/Large (92 cm)",
      shop: "Pets at Home",
      price: "£110",
      link: "https://www.petsathome.com/product/ferplast-superior-dog-crate/P72105",
      why: "Sturdier 92 cm crate with reinforced double-lock doors and a movable divider that grows with him.",
    },
  },
  "crate-bed": {
    value: {
      name: "Pets at Home Explorer Dog Crate Mat Slate Grey Medium (90 x 56 cm)",
      shop: "Pets at Home",
      price: "£18",
      link: "https://www.petsathome.com/product/pets-at-home-explorer-dog-crate-mat-berber-slate-grey/P70226",
      why: "Sized for a 36-inch crate, soft berber top, washable and rated 4.8 by 185 owners. Buy two to swap.",
    },
    premium: {
      name: "Petlife Non-Slip Vetbed Grey 91 x 51 cm",
      shop: "Pets at Home",
      price: "£27",
      link: "https://www.petsathome.com/product/petlife-non-slip-vetbed-grey/P4601",
      why: "Genuine Vetbed: wicks wee away, dries fast and survives endless hot washes. Ideal for toilet-training weeks.",
    },
  },
  "crate-cover": {
    value: {
      name: "Pets at Home Dog Crate Cover Black Medium (92 cm)",
      shop: "Pets at Home",
      price: "£14",
      link: "https://www.petsathome.com/product/pets-at-home-dog-crate-cover-black/P71646",
      why: "Cheap fitted cover made for a 92 cm crate, so it fits the crates above without bunching.",
    },
    premium: {
      name: "Midwest Homes for Pets Quiet Time Defender Dog Crate Cover Grey Medium/Large (91.4 cm)",
      shop: "Pets at Home",
      price: "£26",
      link: "https://www.petsathome.com/product/midwest-homes-for-pets-quiet-time-defender-dog-crate-cover-grey/P70159",
      why: "Fits 36-inch crates; breathable fabric, and fastener tabs let you leave the front open or reach any door.",
    },
  },
  bed: {
    value: {
      name: "Pets at Home Oval Dog Bed Dark Grey Large (73 x 56 cm)",
      shop: "Pets at Home",
      price: "£40",
      link: "https://www.petsathome.com/product/pets-at-home-teddy-oval-dog-bed-dark-grey/P70983",
      why: "Raised sides for curling up, machine-washable cover, rated 4.7. Cheap enough to replace if he chews it.",
    },
    premium: {
      name: "Charley Chau Dog Snuggle Bed in Weave II, Medium",
      shop: "Charley Chau",
      price: "£160",
      link: "https://www.charleychau.com/products/dog-snuggle-bed-in-weave-ii",
      why: "British-made bolstered bed with a removable washable cover; a lasting buy once he’s past chewing.",
    },
  },
  blankets: {
    value: {
      name: "Pets at Home Snuggle Plush Pet Blanket Mocha (100 x 70 cm)",
      shop: "Pets at Home",
      price: "£5",
      link: "https://www.petsathome.com/product/pets-at-home-snuggle-plush-pet-blanket-mocha/7159685P",
      why: "Soft, cheap and washable, so you can buy a few and rotate them through the wash.",
    },
    premium: {
      name: "Pets at Home Woolly Dog Blanket Cream 100 x 80 cm",
      shop: "Pets at Home",
      price: "£15",
      link: "https://www.petsathome.com/product/pets-at-home-teddy-woolly-dog-blanket-throw-cream-100x80cm/7143591P",
      why: "Super-soft woolly throw, machine washable, nice enough for the sofa too. Rated 5 stars.",
    },
  },
  playpen: {
    value: {
      name: "Metal Run for Small Pet & Puppies – 8 Sided, 8 panels each 80 x 80 cm",
      shop: "Zooplus",
      price: "£71.89",
      link: "https://www.zooplus.co.uk/shop/small_pets/hutches/runs/523574",
      why: "Eight 80 cm metal panels joined by pins, so you can reshape it or butt it up to the crate door.",
    },
    premium: {
      name: "Aluminium Puppy Playpen, 6 fencing units 80 x 80 cm",
      shop: "Zooplus",
      price: "£166.99",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_kennels_flaps/dog_pens/dog_metal_pens/860616",
      why: "Light, rust-proof aluminium panels with a lockable gate; easy to carry up three flights and store.",
    },
  },
  "comfort-toy": {
    value: {
      name: "Petlando Moodles Bear (approx. 40 cm)",
      shop: "Zooplus",
      price: "£10.59",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_toys_dog_training/puppy_toys/1672827",
      why: "Big, soft puppy cuddle toy with no squeaker. Rub it on the litter blanket so it smells of home.",
    },
    premium: {
      name: "Snuggle Puppy Dog Comforter with Heartbeat, Soft Biscuit",
      shop: "Treat Your Dog",
      price: "£34.99",
      link: "https://www.treatyourdog.co.uk/products/snuggle-puppy-biscuit-dog",
      why: "The original heartbeat toy with a warm pack; mimics a littermate for his first nights. Rated 4.7 by 373.",
    },
  },
  "breeder-food": {
    value: {
      name: "Country Dog Premium Puppy Hypoallergenic Chicken & Rice Dry Puppy Food 10 kg",
      shop: "Mole Valley",
      price: "£27.49",
      link: "https://www.moleonline.com/country-dog-premium-puppy-hypoallergenic-chicken-rice-dry-puppy-food-10kg-1154861",
      why: "What the breeder feeds, so his tummy stays settled through the move. Very good price for 10 kg.",
    },
    premium: {
      name: "Royal Canin Cocker Puppy Dry Dog Food 3 kg",
      shop: "Pets at Home",
      price: "£21.89",
      link: "https://www.petsathome.com/product/royal-canin-cocker-dry-puppy-food/7102508P",
      why: "Made for cocker puppies, with support for skin, coat, digestion and teeth. Rated 4.9 by 48 owners.",
    },
  },
  "new-food": {
    value: {
      name: "PRO PLAN Medium Puppy Healthy Start Dry Dog Food Chicken 12 kg",
      shop: "Pets at Home",
      price: "£46.39 for 12 kg",
      link: "https://www.petsathome.com/product/pro-plan-healthy-start-medium-dry-puppy-food-chicken/P4653",
      why: "Vet-favoured complete food for medium-breed puppies, chicken-based like his current food. Rated 4.8.",
    },
    premium: {
      name: "Royal Canin Size Health Nutrition Medium Dry Puppy Food 15 kg",
      shop: "Pets at Home",
      price: "£66.99 for 15 kg",
      link: "https://www.petsathome.com/product/royal-canin-size-health-medium-breed-dry-puppy-food/19121P",
      why: "Precisely balanced for medium breeds up to 12 months; very consistent and rated 4.9 by 112 owners.",
    },
  },
  "spaniel-bowl": {
    value: {
      name: "Pets at Home Long-Eared Stainless Steel Dog Bowl",
      shop: "Pets at Home",
      price: "£7.50",
      link: "https://www.petsathome.com/product/pets-at-home-long-eared-stainless-steel-dog-bowl/7155869P",
      why: "Angled sides and narrow top keep his ears out of the food; non-slip base and dishwasher safe.",
    },
    premium: {
      name: "Mason Cash Non-Tip Spaniel Bowl Cream (21 cm)",
      shop: "Pets at Home",
      price: "£10",
      link: "https://www.petsathome.com/product/mason-cash-non-tip-spaniel-bowl-cream/7155955P",
      why: "The classic heavy stoneware spaniel bowl: tapered for long ears and almost impossible to tip.",
    },
  },
  "slow-feeder": {
    value: {
      name: "Pets at Home Grey Slow Feeder Bowl Small/Medium",
      shop: "Pets at Home",
      price: "£8",
      link: "https://www.petsathome.com/product/pets-at-home-grey-slow-feeder-bowl-small-medium/7155868P",
      why: "Shallow 3.5 cm bowl with low ridges a small puppy can manage, plus a non-slip base.",
    },
    premium: {
      name: "Mason Cash Cane Slow Feeder Dog Bowl",
      shop: "Pets at Home",
      price: "£12",
      link: "https://www.petsathome.com/product/mason-cash-cane-slow-feeder-dog-bowl/7157754P",
      why: "Heavy stoneware with raised ridges: won’t slide or tip, resists chewing, and goes in the dishwasher.",
    },
  },
  "water-bowl": {
    value: {
      name: "Pets at Home Slip Resistant Stainless Steel Bowl Large (21 cm)",
      shop: "Pets at Home",
      price: "£5",
      link: "https://www.petsathome.com/product/pets-at-home-slip-resistant-stainless-steel-bowl-m/P72417",
      why: "Stainless steel with a silicone non-slip base; hygienic, dishwasher safe and big enough as he grows.",
    },
    premium: {
      name: "Scruffs Scandi Non Tip Large Dog Bowl Cream 20 cm",
      shop: "Pets at Home",
      price: "£17",
      link: "https://www.petsathome.com/product/scruffs-scandi-non-tip-dog-bowl-cream/P72355",
      why: "Heavy stoneware with a flared base so it won’t tip, keeps water cool and looks smart in the flat.",
    },
    tip: "A clip-on crate bowl stops spills overnight — worth a look if he tips these.",
  },
  "kitchen-scale": {
    value: {
      name: "Salter Disc Digital Kitchen Scale",
      shop: "Salter",
      price: "£17.99",
      link: "https://salter.com/products/disc-digital-kitchen-scale.html",
      why: "Weighs to the gram with a zero (tare) button, so you can weigh meals straight into his bowl.",
    },
    premium: {
      name: "Joseph Joseph TriScale Digital Kitchen Scales White",
      shop: "Joseph Joseph",
      price: "£42.50",
      link: "https://www.josephjoseph.com/products/triscale-white",
      why: "Weighs in 1 g steps with add-and-weigh, and folds flat into a drawer in a small flat kitchen.",
    },
  },
  "food-bin": {
    value: {
      name: "TIAKI Food Container 40 Litres",
      shop: "Zooplus",
      price: "£18.99",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_bowls_feeders/food_storage/2460075",
      why: "Airtight lid and wheels, and big enough to hold a whole bag of food in its original packaging.",
    },
    premium: {
      name: "Ferplast Feedy Food Bin 39 Litres (up to 15 kg dry food)",
      shop: "Zooplus",
      price: "£32.39",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_bowls_feeders/food_storage/648404",
      why: "Sturdier sealed bin that takes a 12–15 kg bag, rated 5 stars by 159 owners.",
    },
  },
  "training-treats": {
    value: {
      name: "Wainwright's Puppy Turkey and Rice with Vegetables Semi Moist Training Treats 140g",
      shop: "Pets at Home",
      price: "£2.49",
      link: "https://www.petsathome.com/product/wainwrights-semi-moist-puppy-training-treats-turkey-and-rice-with-vegetables/7146788P",
      why: "Soft, puppy-sized and easy to break into pea-sized bits; cheap enough to use lots in training.",
    },
    premium: {
      name: "Lily's Kitchen Puppy Chicken and Salmon Dog Treats 70g",
      shop: "Pets at Home",
      price: "£4.19",
      link: "https://www.petsathome.com/product/lilys-kitchen-nibbles-puppy-treats-chicken-and-salmon/7139350P",
      why: "Soft, grain-free nibbles of real chicken and salmon, sized for puppies; great for recall.",
    },
  },
  "chew-treats": {
    value: {
      name: "Whimzees by Wellness Puppy Daily Dental Treats X Small/Small 28 Pack",
      shop: "Pets at Home",
      price: "£7.49",
      link: "https://www.petsathome.com/product/whimzees-puppy-natural-daily-dental-treats-x-small-small-28-pack/7148316P",
      why: "Plant-based, puppy-sized chews suitable from about 3 months; no rawhide. Supervise while he chews.",
    },
    premium: {
      name: "YAKERS Original Yak Milk Dog Chew and Occupier Medium",
      shop: "Pets at Home",
      price: "£5.99",
      link: "https://www.petsathome.com/product/yakers-original-dog-chew-and-occupier-medium/7128083P",
      why: "Natural yak-milk chew that lasts for weeks; better from about 5 months once his adult teeth come.",
    },
  },
  collar: {
    value: {
      name: "Halti Anatomy Dog Collar, Grey",
      shop: "Pets at Home",
      price: "£13.99",
      link: "https://www.petsathome.com/product/halti-anatomy-dog-collar-grey/P72423",
      why: "Light, padded neoprene collar with one easy adjuster and reflective stitching; X Small/Small fits a growing pup.",
    },
    premium: {
      name: "Ruffwear Front Range Dog Collar",
      shop: "Ruffwear UK",
      price: "£24.95",
      link: "https://ruffwear.co.uk/products/front-range-everyday-dog-collar",
      why: "Tough, lightweight webbing with a sturdy buckle and reflective trim; built to last into adulthood.",
    },
  },
  "id-tag": {
    value: {
      name: "Tagiffany Silicone Slider Dog Collar Tag (Black)",
      shop: "Chelsea Dogs",
      price: "£9.99",
      link: "https://www.chelseadogs.com/products/silicone-slider-dog-collar-tag-a-black-pet-id-tag",
      why: "Engraved steel plate sits flat on the collar, so it won't jingle or snag in undergrowth. Pick his collar width.",
    },
    premium: {
      name: "Chunky Brass Hooped Designer Dog Tag (25mm)",
      shop: "Chelsea Dogs",
      price: "£12.99",
      link: "https://www.chelseadogs.com/products/chunky-brass-hooped-designer-dog-tag",
      why: "Thick 2.2mm solid brass, deep-engraved free on both sides, with room for surname, address, postcode and phone.",
    },
  },
  harness: {
    value: {
      name: "PerfectFit Cocker (English) Harness",
      shop: "PerfectFit",
      price: "£35.70",
      link: "https://perfect-fit-dog-harness.com/products/cocker-english-harness",
      why: "UK-made fleece Y-front harness with front and back rings; swap single pieces as he grows instead of buying a new one.",
    },
    premium: {
      name: "Ruffwear Front Range Dog Harness",
      shop: "Ruffwear UK",
      price: "£49.95",
      link: "https://ruffwear.co.uk/products/front-range-everyday-dog-harness",
      why: "Padded Y-front design with a back V-ring and front clip, five adjusters and reflective trim; very hard-wearing.",
    },
  },
  lead: {
    value: {
      name: "Halti Training Dog Lead Small Black",
      shop: "Pets at Home",
      price: "£10",
      link: "https://www.petsathome.com/product/halti-training-dog-lead-black-small/7118311P",
      why: "2 m double-ended lead with two clips and several lengths; the Small size is light enough for a puppy.",
    },
    premium: {
      name: "PerfectFit Training Lead 2m",
      shop: "PerfectFit",
      price: "£24.16",
      link: "https://perfect-fit-dog-harness.com/products/training-lead",
      why: "Soft fleece double-ended lead with four length options; clips to front and back rings for gentle steering.",
    },
  },
  "long-line": {
    value: {
      name: "Pets at Home Waterproof Training Line With Storage Bag Dog Yellow 10 Metre",
      shop: "Pets at Home",
      price: "£20",
      link: "https://www.petsathome.com/product/pets-at-homewaterproof-training-line-with-storage-bag-dog-yellow-10-metre/7154230P",
      why: "10 m waterproof, wipe-clean line with a locking clip; doesn't soak up mud. Coated webbing rather than true biothane.",
    },
    premium: {
      name: "Heim Biothane Long Dog Lead - Fluorescent Yellow (5m)",
      shop: "Zooplus",
      price: "£26.89",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_collars_dog_leads/nylon_leads/special/194643",
      why: "Genuine Biothane: waterproof, won't tangle or smell, and easy to spot in grass. 10 m version is £47.59.",
    },
  },
  "poo-bags": {
    value: {
      name: "Just For Puppy Easy Fill Poop Bag Dispenser with 240 Poop Bags",
      shop: "Pets at Home",
      price: "£8",
      link: "https://www.petsathome.com/product/just-for-puppy-easy-fill-poop-bag-dispenser-with-240-poop-bags/7146399P",
      why: "Clip-on lead dispenser plus 240 tie-handle bags with at least 30% recycled content, all in one buy.",
    },
    premium: {
      name: "Ruffwear Stash Bag Mini Pickup Bag Dispenser",
      shop: "Ruffwear UK",
      price: "£24.95",
      link: "https://ruffwear.co.uk/products/stash-bag-mini",
      why: "Quiet zip dispenser that fixes to the lead at two points, with a loop for used bags. Comes with Earth Rated bags.",
    },
  },
  "treat-pouch": {
    value: {
      name: "Pets at Home Silicone Easy Clean Dog Treat Pouch Grey",
      shop: "Pets at Home",
      price: "£5",
      link: "https://www.petsathome.com/product/pets-at-home-silicone-easy-clean-dog-treat-pouch-grey/7154216P",
      why: "Clip-on silicone pouch with a magnetic closure for one-handed use. Dishwasher-safe, and cheap enough to buy two.",
    },
    premium: {
      name: "Ruffwear Treat Trader Dog Treat Pouch",
      shop: "Ruffwear UK",
      price: "£49.95",
      link: "https://ruffwear.co.uk/products/treat-trader-dog-treat-pouch",
      why: "Magnetic one-hand opening, waterproof lining, belt or clip-on wear and a zip pocket for bags and keys.",
    },
  },
  whistle: {
    value: {
      name: "ACME 210 1/2 Whistle - Black",
      shop: "TFM Farm & Country Superstore",
      price: "£8.99",
      link: "https://www.tfmsuperstore.co.uk/products/acme-210-1-2-whistle/",
      why: "The classic spaniel whistle: pea-less, single high tone. Buy two and add lanyards (not included).",
    },
    premium: {
      name: "ACME ALPHA 210.5 Dog Whistle",
      shop: "Sporting Saint",
      price: "£12.49",
      link: "https://www.sportingsaint.co.uk/product/acme-alpha-210-5-dog-whistles/",
      why: "Same 210.5 pitch spaniel trainers use, with better grip and easier, more consistent blowing. Lanyard sold separately.",
    },
  },
  "reflective-light": {
    value: {
      name: "Pets at Home Rechargeable Blinker Light Dog Collar Orange",
      shop: "Pets at Home",
      price: "£6",
      link: "https://www.petsathome.com/product/pets-at-home-rechargeable-blinker-light-dog-collar-orange/7147819P",
      why: "USB-rechargeable, rainproof clip-on light with three modes; well reviewed and handy for dark Glasgow winter walks.",
    },
    premium: {
      name: "Ruffwear The Beacon Dog Safety Light",
      shop: "Ruffwear UK",
      price: "£36.95",
      link: "https://ruffwear.co.uk/products/the-beacon-safety-dog-light",
      why: "Bright, waterproof, USB-rechargeable light that clips to the collar or the Front Range harness light loop.",
    },
  },
  "car-restraint": {
    value: {
      name: "CarSafe Crash Tested Dog Harness Blue",
      shop: "Pets at Home",
      price: "£27.50",
      link: "https://www.petsathome.com/product/carsafe-crash-tested-car-restraint-dog-harness-blue/P70804",
      why: "Crash-tested harness that links straight to the seat belt and doubles as a walking harness. Size up as he grows.",
    },
    premium: {
      name: "KONG Ultimate Car Safety Dog Harness",
      shop: "Pets at Home",
      price: "£40",
      link: "https://www.petsathome.com/product/kong-ultimate-durable-crash-tested-car-safety-dog-harness/P72723",
      why: "Crash-tested to ASTM F3725 and FMVSS 213 conditions, with heavy-duty webbing and a direct seat-belt attachment.",
    },
  },
  insurance: {
    value: {
      name: "Animal Friends Lifetime Dog Insurance",
      shop: "Animal Friends",
      price: "Quote online",
      link: "https://www.animalfriends.co.uk/dog/lifetime-dog-insurance/",
      why: "Lifetime cover with annual vet-fee limits up to £18,000. Check waiting periods and pre-existing conditions.",
    },
    premium: {
      name: "Petplan Covered for Life Dog Insurance",
      shop: "Petplan",
      price: "Quote online",
      link: "https://www.petplan.co.uk/dog-insurance/covered-for-life.html",
      why: "Top-tier lifetime policy, up to £12,000 a year with a claims pricing guarantee and direct vet payment.",
    },
  },
  "first-aid-kit": {
    value: {
      name: "Advanced Pet First Aid Kit in a Pouch",
      shop: "ProTrainings",
      price: "£22.74",
      link: "https://www.protrainings.uk/products/advanced-pet-first-aid-kit-in-a-pouch",
      why: "Has saline wipes, non-stick dressings, cohesive bandage, scissors, tweezers, tick remover and a foil blanket.",
    },
    premium: {
      name: "Charlie the Vet Pet First Aid Kit",
      shop: "Pets Corner",
      price: "£34.99",
      link: "https://www.petscorner.co.uk/dog/dog-travel/charlie-the-vet-first-aid-kit/",
      why: "Vet-designed 45-item kit with bandages, two tick removers, saline, a thermometer, a foil blanket and instructions.",
    },
  },
  "tick-twister": {
    value: {
      name: "O'Tom Tick Twister Remover",
      shop: "Pets at Home",
      price: "£4.50",
      link: "https://www.petsathome.com/product/otom-tick-twister-remover/7123631P",
      why: "The vet-favourite twist-out hook, in two sizes, that lifts out the whole tick without squeezing it.",
    },
    premium: {
      name: "Vet's Best 2-in-1 Tick Remover",
      shop: "Pets at Home",
      price: "£5",
      link: "https://www.petsathome.com/product/vets-best-2-in-1-tick-remover/7153281P",
      why: "Solid stainless steel tweezers plus a slotted scoop for big and tiny ticks; durable enough to keep on the lead.",
    },
  },
  "parasite-control": {
    value: {
      name: "NexGard Spectra Chewable Tablets for Dogs (pack of 3)",
      shop: "Animed",
      price: "£18.78",
      link: "https://www.animed.co.uk/nexgard-spectra-chewable-pack-of-3-tablets",
      why: "Needs a vet prescription. Monthly chew for fleas, ticks and roundworms, and prevents lungworm; dose by weight.",
    },
    premium: {
      name: "University of Glasgow Pet Practice Health Plan",
      shop: "University of Glasgow Pet Practice",
      price: "£25.99 a month",
      link: "https://www.gla.ac.uk/schools/bohvm/sah/petpractice/healthplan/",
      why: "Glasgow vet plan: unlimited consults, vaccines, and vet-prescribed flea, tick and worm treatment. Ask about lungworm.",
    },
    tip: "NexGard Spectra is prescription-only, so your vet has to prescribe it. Ask the practice whether the plan covers lungworm too.",
  },
  "pet-carrier": {
    value: {
      name: "Just for Puppy Quilted Folding Pet Carrier Grey",
      shop: "Pets at Home",
      price: "£20",
      link: "https://www.petsathome.com/product/just-for-puppy-quilted-folding-carrier-grey/7154271P",
      why: "Physio-approved soft carrier for up to 7 kg; folds flat for the flat once he outgrows it.",
    },
    premium: {
      name: "FuzzYard Life Sling Carrier - Slate Grey",
      shop: "Chelsea Dogs",
      price: "£49.99",
      link: "https://www.chelseadogs.com/products/fuzzyard-life-sling-carrier-slate-grey",
      why: "Washable cotton sling with a wide strap and harness safety clip, rated to about 10 kg, so it lasts longer.",
    },
  },
  "slicker-brush": {
    value: {
      name: "Pets at Home Short Rounded Pin Slicker Brush for Dogs & Cats",
      shop: "Pets at Home",
      price: "£8",
      link: "https://www.petsathome.com/product/pets-at-home-short-rounded-pin-slicker-brush-for-dogs-and-cats/7148335P",
      why: "Short, rounded pins are kind to puppy skin, so brushing is a good experience while he gets used to it.",
    },
    premium: {
      name: "Doggyman Small Slicker Brush",
      shop: "Groomers Online",
      price: "£9.95",
      link: "https://www.groomers-online.com/products/doggyman-small-slicker-brush",
      why: "A groomer's slicker with steel pins set in a soft, flexible pad. Gentle on his silky ears and feathering.",
    },
  },
  "metal-comb": {
    value: {
      name: "Groomers Metal Combination Comb (Simply G)",
      shop: "Groomers Online",
      price: "£5.95",
      link: "https://www.groomers-online.com/products/groomers-metal-combination-comb",
      why: "Stainless steel with medium and fine teeth with rounded tips, for finding knots in his ears and feathering.",
    },
    premium: {
      name: "Groomers Combination Comb 19cm Dark Blue",
      shop: "Groomers Online",
      price: "£12.95",
      link: "https://www.groomers-online.com/products/groomers-combination-comb-19cm-dark-blue",
      why: "Light aluminium handle with round-ended stainless pins. Comfortable for daily combing of spaniel ears and feathers.",
    },
  },
  "dog-towel": {
    value: {
      name: "Pets at Home Microfibre Towel for Dogs & Cats",
      shop: "Pets at Home",
      price: "£10",
      link: "https://www.petsathome.com/product/pets-at-home-microfibre-towel-for-dogs-and-cats/7148318P",
      why: "Absorbent microfibre that dries fast. Good for rubbing down wet ears and legs after Glasgow walks.",
    },
    premium: {
      name: "Ruff and Tumble Classic Dog Drying Coat - Forest",
      shop: "Ruff and Tumble",
      price: "£37",
      link: "https://ruffandtumbledogcoats.com/products/classic-dog-drying-coat-forest-green",
      why: "Thick British cotton towelling coat that dries him while he walks up three flights. Buy once he's nearer adult size.",
    },
    tip: "The drying coat is priced by size — measure his back before you order.",
  },
  "ear-cleaner": {
    value: {
      name: "DOUXO S3 CARE Ear Cleaner for Dogs & Cats 120ml",
      shop: "Pets at Home",
      price: "£13",
      link: "https://www.petsathome.com/product/douxo-s3-care-ear-cleaner-for-dogs-and-cats-120ml/7154960P",
      why: "A gentle, vet-brand ear cleaner for routine cleaning of his long, heavy ears.",
    },
    premium: {
      name: "Dechra CleanAural Dog Ear Cleaner 100ml",
      shop: "Pets at Home",
      price: "£17.50",
      link: "https://www.petsathome.com/product/dechra-cleanaural-ear-cleaner-dog-100ml/P4193",
      why: "A gentle cleaner that vets often stock. Good for regular use on cocker ears, which are prone to wax and infection.",
    },
    tip: "Ask your vet which cleaner they’d like you to use, and don’t clean an ear that looks sore or smells.",
  },
  "cotton-pads": {
    value: {
      name: "Morrisons Pure Round Cotton Pads",
      shop: "Morrisons",
      price: "£1.60",
      link: "https://groceries.morrisons.com/products/morrisons-pure-round-cotton-pads/108639640",
      why: "Plain 100% cotton pads for wiping cleaner from the ear flap and the opening of the ear canal.",
    },
    premium: {
      name: "kooa Sensitive Ear & Eye Finger Pads",
      shop: "Zooplus",
      price: "£7.39",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_grooming_care/dog_ear_eye_lotion/ear_care/1336457",
      why: "Dog-specific pads that slip over your finger, giving more control when cleaning the folds of his ears.",
    },
    tip: "Any plain cotton pads do; the finger pads are a handy extra rather than a must.",
  },
  "nail-clippers": {
    value: {
      name: "Pets at Home Dog Claw Clippers Small",
      shop: "Pets at Home",
      price: "£8",
      link: "https://www.petsathome.com/product/pets-at-home-claw-clippers-for-dogs-small/7148319P",
      why: "Small, comfortable-grip clippers with a safety guard to stop you cutting too much off a puppy nail.",
    },
    premium: {
      name: "FURminator Nail Grinder Grooming Tool For Cats & Dogs",
      shop: "Pets at Home",
      price: "£24",
      link: "https://www.petsathome.com/product/furminator-dog-and-cat-nail-grinder/7145494P",
      why: "Cordless two-speed grinder with an LED light to show the quick. Smooth edges and less risk of a nick.",
    },
    tip: "Keep styptic powder in case you nick the quick — e.g. Aqueos Blood Stop, about £14.50 at Groomers Online.",
  },
  toothbrush: {
    value: {
      name: "Dentifresh Puppy & Kitten Toothpaste & Finger Brush",
      shop: "Pets at Home",
      price: "£5",
      link: "https://www.petsathome.com/product/puppy-and-kitten-toothpaste-and-finger-brush/7103042P",
      why: "Soft rubber finger brush and meat-flavoured puppy toothpaste: an easy start while he's teething.",
    },
    premium: {
      name: "Virbac Dog & Cat Toothbrush Kit with Poultry Flavour Toothpaste 70g",
      shop: "Pets at Home",
      price: "£15",
      link: "https://www.petsathome.com/product/virbac-dog-and-cat-toothpaste-and-toothbrush-kit-poultry/7113237P",
      why: "Vet-brand enzymatic toothpaste with a proper dog toothbrush, ready for when his adult teeth arrive.",
    },
  },
  shampoo: {
    value: {
      name: "Animology Puppy Love Shampoo 250ml",
      shop: "Pets at Home",
      price: "£5.85",
      link: "https://www.petsathome.com/product/animology-puppy-love-shampoo-250ml/26447P",
      why: "Mild puppy shampoo from 6 weeks, with built-in conditioner to help stop his silky coat tangling.",
    },
    premium: {
      name: "Douxo Skin & Coat Spa Ultra-Safe Puppy Shampoo 250ml",
      shop: "Pets at Home",
      price: "£12",
      link: "https://www.petsathome.com/product/douxo-skin-and-coat-spa-ultra-safe-puppy-shampoo-250ml/7157513P",
      why: "Free of soap, sulphates and parabens, with a prebiotic to protect delicate puppy skin. A vet-recommended brand.",
    },
  },
  "grooming-table": {
    value: {
      name: "Anti-Slip Rubber Table Mat (120 x 60cm)",
      shop: "Groomers Online",
      price: "£39.95",
      link: "https://www.groomers-online.com/products/groomers-anti-slip-rubber-table-mat",
      why: "Non-slip rubber top that turns a sturdy table into a safe grooming spot. Easy to roll up and store in a flat.",
    },
    premium: {
      name: "Groomers Portable Trolley Table",
      shop: "Groomers Online",
      price: "£164.96",
      link: "https://www.groomers-online.com/products/groomers-portable-trolley-table",
      why: "Pro folding table with non-slip top and grooming arm. Folds into a wheeled trolley, so it stores well in a flat.",
    },
    tip: "A non-slip mat on a sturdy table you already have works fine while he’s small.",
  },
  "grooming-scissors": {
    value: {
      name: "Pets at Home Grooming Scissors for Dogs & Cats (Small)",
      shop: "Pets at Home",
      price: "£5",
      link: "https://www.petsathome.com/product/pets-at-home-grooming-scissors-for-dogs-and-cats/P71320",
      why: "Small scissors with rounded tips for tidying round his paws and ear edges between groomer visits.",
    },
    premium: {
      name: "Kanpeki Kingfisher 4.5\" Straight Bull Nose Scissors",
      shop: "Groomers Online",
      price: "£21.60",
      link: "https://www.groomers-online.com/products/kanpeki-kingfisher-45-straight-bull-nose-scissors",
      why: "Groomer-quality Japanese steel with a bull-nose (round) tip. Short and safe near eyes, ears and feet.",
    },
  },
  kong: {
    value: {
      name: "KONG Puppy Chew Treat Single Dog Toy (Medium)",
      shop: "Pets at Home",
      price: "£9",
      link: "https://www.petsathome.com/product/kong-puppy-chew-treat-toy/P276",
      why: "Medium size is for 7–16 kg puppies, which suits a cocker. Soft puppy rubber. Buy two so one can be frozen.",
    },
    premium: {
      name: "KONG Puppy Activity Ball Natural Rubber Dog Toy (Medium)",
      shop: "Pets at Home",
      price: "£10",
      link: "https://www.petsathome.com/product/kong-puppy-activity-ball-natural-rubber-dog-toy-medium/7117883P",
      why: "Same gentle puppy rubber but it rolls and drops kibble, so it also works as a feeding game.",
    },
    tip: "There isn’t really a “premium” KONG Puppy — the Activity Ball is its sister toy.",
  },
  "lick-mat": {
    value: {
      name: "LickiMat Buddy Boredom Buster and Slow Feeder Orange",
      shop: "Pets at Home",
      price: "£6",
      link: "https://www.petsathome.com/product/lickimat-buddy-boredom-buster-orange/7125587P",
      why: "The classic textured rubber lick mat. Cheap, effective, and calming when he's in the crate.",
    },
    premium: {
      name: "LickiMat Splash",
      shop: "The Pet Health Club",
      price: "£13.99",
      link: "https://www.shop.thepethealthclub.co.uk/products/lickimat-splash-various-colours",
      why: "Strong suction cup holds it on the bath or tiles to distract him while you wash or dry him. Dishwasher-safe.",
    },
  },
  "snuffle-mat": {
    value: {
      name: "Sniffing Carpet Sunnyflower (Ø 50cm)",
      shop: "Zooplus",
      price: "£12.39",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_toys_dog_training/intelligence_toys/1672963",
      why: "Machine-washable fleece flower mat for scattering kibble. A good way to use a spaniel's nose indoors.",
    },
    premium: {
      name: "Trixie Activity Sniffer Carpet (50 x 34cm)",
      shop: "Zooplus",
      price: "£22.94",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_toys_dog_training/intelligence_toys/classic_puzzles/675691",
      why: "Two fringe lengths for easy and harder searches, non-slip base, removable plate, and washable at 30°C.",
    },
    tip: "Supervise him with it so he doesn’t chew off and swallow the strips.",
  },
  "tug-toy": {
    value: {
      name: "Coachi Puppy & Mini Dog Training Tuggi Tug Toy Teal & Mint",
      shop: "Pets at Home",
      price: "£12.50",
      link: "https://www.petsathome.com/product/coachi-puppy-and-mini-small-dog-breed-dog-training-tuggi-tug-toy-teal-and-mint/7155701P",
      why: "Soft, stretchy puppy-size tug with a long handle that keeps your hands away from his teeth.",
    },
    premium: {
      name: "Tug-E-Nuff Sheepskin Bungee Chaser",
      shop: "Tug-E-Nuff",
      price: "£26.95",
      link: "https://tug-e-nuff.com/products/sheepskin-bungee-chaser",
      why: "Real sheepskin on a long bungee handle. Very appealing to a gundog, and good for building recall through play.",
    },
  },
  "soft-toys": {
    value: {
      name: "Wainwright's Puppy Pheasant Flattie Dog Toy",
      shop: "Pets at Home",
      price: "£6",
      link: "https://www.petsathome.com/product/wainwrights-puppy-pheasant-flattie/7146503P",
      why: "Stuffing-free with squeak and crinkle. The pheasant suits a gundog puppy, and there's no filling to pull out.",
    },
    premium: {
      name: "Bite Force Kevlar Tough Plush Fox Squeak & Crinkle Dog Toy",
      shop: "Pets at Home",
      price: "£13.99",
      link: "https://www.petsathome.com/product/bite-force-kevlar-tough-plush-fox-squeak-and-crinkle-dog-toy/7156955P",
      why: "Kevlar yarn and reinforced seams make it last much longer than normal plush once his adult teeth arrive.",
    },
    tip: "No soft toy is chew-proof — check them often and take them away if a seam opens.",
  },
  "teething-toys": {
    value: {
      name: "Nylabone Freezer Bone Puppy Chew Toy (Medium)",
      shop: "Pets at Home",
      price: "£8",
      link: "https://www.petsathome.com/product/nylabone-freezer-bone-puppy-chew-toy-medium/7139637P",
      why: "Made to go in the freezer, so it cools and soothes sore gums while he's teething.",
    },
    premium: {
      name: "KONG Puppy Binkie Natural Rubber Dog Toy (Small)",
      shop: "Pets at Home",
      price: "£11",
      link: "https://www.petsathome.com/product/kong-puppy-binkie-natural-rubber-dog-toy-small/7140208P",
      why: "Soft KONG puppy rubber that you can stuff and freeze. Eases teething and keeps him busy.",
    },
  },
  "puzzle-feeder": {
    value: {
      name: "Trixie Dog Activity Flip Board Toy",
      shop: "Zooplus",
      price: "£10.09",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_toys_dog_training/intelligence_toys/classic_puzzles/346553",
      why: "Lift-off cones for a first easy game, then flaps and sliders as he learns. Dishwasher-safe.",
    },
    premium: {
      name: "Nina Ottosson Dog Smart Orange",
      shop: "The Pet Health Club",
      price: "£15.99",
      link: "https://www.shop.thepethealthclub.co.uk/products/nina-ottosson-dog-smart-orange",
      why: "A true level-one puzzle from the best-known puzzle maker: he nudges covers off nine treat wells.",
    },
  },
  "puppy-dummy": {
    value: {
      name: "Sporting Saint 1/2lb Canvas Dummy",
      shop: "Sporting Saint",
      price: "£8.50",
      link: "https://www.sportingsaint.co.uk/product/1-2lb-canvas-dummy/",
      why: "Classic UK-made ½ lb (about 227 g) canvas dummy. The right weight to start retrieves with a cocker pup.",
    },
    premium: {
      name: "Sporting Saint BG True Centre Hold Dummy - 1/2lb",
      shop: "Sporting Saint",
      price: "£9.50",
      link: "https://www.sportingsaint.co.uk/product/bg-true-centre-hold-dummy-1-2lb/",
      why: "Ben Randall design that teaches a central, soft hold from the start. Same ½ lb weight.",
    },
  },
  "door-grab-bag": {
    value: {
      name: "3 Peaks Water Repellent Dog Walking Bum Bag Green",
      shop: "Pets at Home",
      price: "£11",
      link: "https://www.petsathome.com/product/3-peaks-water-repellent-dog-walking-bum-bag-green/7150003P",
      why: "Small shower-proof bag with a poo-bag dispenser; hang it by the door ready to grab with the lead.",
    },
    premium: {
      name: "3 Peaks Water Repellent Cross Body Dog Walking Bag Black",
      shop: "Pets at Home",
      price: "£24",
      link: "https://www.petsathome.com/product/3-peaks-water-repellent-cross-body-dog-walking-bag-black/7150004P",
      why: "Bigger cross-body bag with waterproof zips, treat pocket and poo-bag dispenser, built for Glasgow weather.",
    },
    tip: "Neither comes with a hook — IKEA’s KUBBIS rack (about £7) does the job.",
  },
  "door-pads": {
    value: {
      name: "zooplus Puppy Trainer Pads, 30 x Large (60 x 45 cm)",
      shop: "Zooplus",
      price: "£5.19",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_grooming_care/dog_toilet/training_pads/741854",
      why: "Cheap, odour-neutral pads with no attractant, which suits a backup-only spot.",
    },
    premium: {
      name: "Trixie Hygienic Underlay 60 x 60 cm (washable)",
      shop: "Zooplus",
      price: "£11.69",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_grooming_care/dog_toilet/1672695",
      why: "Reusable, unscented pad that washes at 60°C, so there are no throwaway pads piling up.",
    },
    tip: "Pick unscented pads: scented ones teach him to wee on anything pad-like.",
  },
  "baby-gates": {
    value: {
      name: "Dreambaby Ava Metal Safety Gate Charcoal (Fits Gaps 75-81cm) Pressure Mounted",
      shop: "Robert Dyas",
      price: "£32.99",
      link: "https://www.robertdyas.co.uk/dreambaby-ava-metal-safety-gate-charcoal-fits-gaps-75-81cms-pressure-mounted",
      why: "Pressure-fit with a walk-through door, one-hand opening and stay-open mode; no drilling in a rented flat.",
    },
    premium: {
      name: "Bettacare Child and Pet Gate, 75cm - 83cm, Slate Grey, Extra Tall 104cm, Pressure Fit",
      shop: "Robert Dyas",
      price: "£39.99",
      link: "https://www.robertdyas.co.uk/bettacare-child-and-pet-gate-44-75cm-45-83cm-44-slate-grey-44-extra-tall-gate-104cm-in-height-44-pressure-fit-stair-gate",
      why: "104 cm tall, so it will still stop a fully grown, bouncy cocker from hopping over.",
    },
    tip: "Measure each opening first. Pressure gates are for the bottom of stairs, never the top.",
  },
  "enzyme-cleaner": {
    value: {
      name: "Simple Solution Pet Urine Destroyer 1 Litre",
      shop: "Pets at Home",
      price: "£9",
      link: "https://www.petsathome.com/product/simple-solution-urine-destroyer-for-pets-1-litre/33878P",
      why: "Enzyme-and-bacteria formula that breaks down urine so he can't smell it, in a big 1 litre size.",
    },
    premium: {
      name: "Nature's Miracle Dog Urine Stain and Odour Remover 946ml",
      shop: "Zooplus",
      price: "£16.79",
      link: "https://www.zooplus.co.uk/shop/dogs/dog_grooming_care/odour_management/1858075",
      why: "Well-known bio-enzymatic cleaner for carpets, floors and fabrics; strong on old, set-in smells.",
    },
  },
  "cable-protectors": {
    value: {
      name: "D-Line 26mm Black Pre-Split Cable Tidy Tube 1.1m",
      shop: "Screwfix",
      price: "£7.99",
      link: "https://www.screwfix.com/p/d-line-26mm-black-pre-split-cable-tidy-tube-1-1m/80437",
      why: "Pre-split tube that you push cables into, to bundle and cover leads at puppy height. Rated 4.6/5.",
    },
    premium: {
      name: "D-Line Cable Tidy Unit White",
      shop: "Screwfix",
      price: "£13.49",
      link: "https://www.screwfix.com/p/d-line-cable-tidy-unit-white/89272",
      why: "Closed box that hides the whole extension lead and plugs, so there's nothing loose for him to chew.",
    },
  },
  "bitter-spray": {
    value: {
      name: "Grannick's Bitter Apple Chew Deterrent 236ml",
      shop: "Pets at Home",
      price: "£12",
      link: "https://www.petsathome.com/product/grannicks-bitter-apple-chew-deterrent-236ml/7113303P",
      why: "The classic non-toxic bitter apple spray for skirting, cables and furniture legs.",
    },
    premium: {
      name: "TropiClean Chew Stop Behavior Aid Spray For Puppies 236ml",
      shop: "Pets at Home",
      price: "£13.99",
      link: "https://www.petsathome.com/product/tropiclean-chew-stop-behavior-aid-spray-for-puppies/7156666P",
      why: "Puppy formula that mixes bitter, sour and spicy tastes, which works on dogs that ignore bitter apple.",
    },
    tip: "Patch-test on fabric first; some dogs don’t mind the taste.",
  },
  "slug-control": {
    value: {
      name: "25m Copper Slug Tape - 20mm Copper Tape Slug Repellent",
      shop: "B&Q",
      price: "£6.49",
      link: "https://www.diy.com/departments/25m-copper-slug-tape-20mm-copper-tape-slug-repellent-humane-slug-snail-control-slug-tape-copper-repellent-snail-repellent/5056175976966_BQ.prd",
      why: "Poison-free barrier round balcony pot rims; 25 m covers plenty of pots.",
    },
    premium: {
      name: "Vitax Slug Gone Wool Pellets 3.5L",
      shop: "B&Q",
      price: "£11.69",
      link: "https://www.diy.com/departments/vitax-slug-gone-wool-pellets-protect-borders-pots-containers-from-slugs-and-snails-3-5l/5060785688508_BQ.prd",
      why: "Soil Association-approved British wool barrier that also feeds the soil; no metaldehyde.",
    },
    tip: "Don’t let him eat the wool pellets.",
  },
  "nfc-tags": {
    value: {
      name: "25mm Round NFC Sticker with White PVC front - NTAG215",
      shop: "ZipNFC",
      price: "£3.50 for 10",
      link: "https://zipnfc.com/nfc-sticker-25mm-circus-white-pvc-front-ntag215.html",
      why: "Genuine NXP NTAG215 round stickers from a UK NFC specialist, ideal for a door frame.",
    },
    premium: {
      name: "28mm Round NFC HARD PVC Disc / Coin with 3M Glue - NXP NTAG215",
      shop: "ZipNFC",
      price: "£4.40 for 10",
      link: "https://zipnfc.com/nfc-tag-hard-pvc-disc-and-3m-glue-ntag215.html",
      why: "Rigid NTAG215 discs with 3M glue that stand up to knocks and teeth better than paper-thin stickers.",
    },
    tip: "Stick them on wood or plaster — these don’t work on metal.",
  },
  "flic-buttons": {
    value: {
      name: "Flic Button | Three Pack | Black",
      shop: "Flic",
      price: "£84 for 3",
      link: "https://shop.flic.io/products/flic-2-black-three-pack",
      why: "Three Flic 2 buttons at about £28 each: one for the door, one for the crate and a spare.",
    },
    premium: {
      name: "Flic Duo | Double Pack | White",
      shop: "Flic",
      price: "£84 for 2",
      link: "https://shop.flic.io/products/flic-duo-double-pack",
      why: "Newer two-button design with 30+ gestures, so one Duo can log several events (wee, poo, fed).",
    },
    tip: "The Flic starter kit (see Flic hub) already includes three buttons.",
  },
  "flic-hub": {
    value: {
      name: "Flic Button | Starter Kit | White (Flic Hub Mini + 3 Flic Buttons)",
      shop: "Flic",
      price: "£101",
      link: "https://shop.flic.io/products/flic-button-starter-kit",
      why: "Hub Mini plus three buttons for little more than the buttons alone, so it covers the buttons item too.",
    },
    premium: {
      name: "Flic | Starter Kit | Button White | Twist White (Flic Hub Mini + Flic Twist + Flic Button)",
      shop: "Flic",
      price: "£127",
      link: "https://shop.flic.io/products/flic-starter-kit-white",
      why: "Adds a Flic Twist dial, handy for Sonos volume as well as puppy logging.",
    },
    tip: "Check in the Flic app that the Hub Mini can send an “Internet request” before relying on it for Arlo HQ.",
  },
  "tablet-mount": {
    value: {
      name: "Durable Universal Aluminium Tablet Holder iPad Wall Mount - Lockable & Rotatable",
      shop: "B&Q",
      price: "£72.99",
      link: "https://www.diy.com/departments/durable-universal-aluminium-tablet-holder-ipad-wall-mount-lockable-rotatable/4005546979670_BQ.prd",
      why: "Clamp fits 7-13\" tablets (Tab S9 FE included), rotates with portrait/landscape click stops and sits off the wall.",
    },
    premium: {
      name: "Durable Aluminium Tablet Holder iPad Wall Arm Mount Lockable Rotatable",
      shop: "Robert Dyas",
      price: "£172.99",
      link: "https://www.robertdyas.co.uk/durable-aluminium-tablet-holder-ipad-wall-arm-mount-lockable-rotatable",
      why: "Same rotating clamp on a 36.5 cm arm that swivels and tilts, so you can angle the tablet towards the room.",
    },
    tip: "Both are clamps that fit 7–13″ tablets, including the Tab S9 FE.",
  },
  "usb-c-socket": {
    value: {
      name: "LAP 13A 2-Gang SP Switched Socket + 3A 22W 2-Outlet Type A & C USB Charger White",
      shop: "Screwfix",
      price: "£11.19",
      link: "https://www.screwfix.com/p/lap-13a-2-gang-sp-switched-socket-3a-22w-2-outlet-type-a-c-usb-charger-white/113pn",
      why: "USB-C with PD at up to 22 W, plenty for a wall tablet, and rated 4.8/5 on Screwfix.",
    },
    premium: {
      name: "British General 900 Series 13A 2-Gang SP Switched Socket + 3A 45W 2-Outlet Type A & C USB Charger White",
      shop: "Screwfix",
      price: "£24",
      link: "https://www.screwfix.com/p/british-general-900-series-13a-2-gang-sp-switched-socket-3a-45w-2-outlet-type-a-c-usb-charger-white/935pm",
      why: "Slim BG 900 faceplate with 45 W PD on USB-C, which charges laptops too. Rated 4.9/5.",
    },
    tip: "Have an electrician fit it.",
  },
  "gps-tracker": {
    value: {
      name: "Tractive GPS DOG 6",
      shop: "Tractive",
      price: "about £50",
      link: "https://tractive.com/en/pd/gps-tracker-dog",
      why: "Official DOG 6 with live tracking and activity/bark monitoring; with a Premium plan it works with Home Assistant.",
    },
    tip: "The subscription is extra. The standard DOG 6 is the right size — the XL is for dogs over 20 kg.",
  },
  camera: {
    value: {
      name: "UniFi Camera G6 Instant (UVC-G6-INS-W, UK version)",
      shop: "Ubiquiti UK",
      price: "£140",
      link: "https://uk.store.ui.com/uk/en/category/physical-security-compact/products/uvc-g6-ins",
      why: "The 4K WiFi camera you picked; plugs into UniFi Protect with two-way audio and on-camera AI detection.",
    },
    premium: {
      name: "UniFi Camera G6 PTZ (UVC-G6-PTZ)",
      shop: "Ubiquiti UK",
      price: "£315",
      link: "https://uk.store.ui.com/uk/en/category/physical-security-ptz/products/uvc-g6-ptz",
      why: "4K pan-tilt-zoom with auto-tracking, so it follows him round the room instead of covering one fixed view.",
    },
    tip: "Both need your UniFi Protect console. The PTZ needs a PoE+ cable or injector; the Instant is Wi-Fi.",
  },
  "ha-hub": {
    value: {
      name: "Raspberry Pi 5 (4GB)",
      shop: "The Pi Hut",
      price: "£105.60",
      link: "https://thepihut.com/products/raspberry-pi-5",
      why: "In stock and faster than the Green; add the official 27 W PSU (£11.50) and an SSD or good SD card.",
    },
    premium: {
      name: "Home Assistant Green",
      shop: "The Pi Hut",
      price: "£159",
      link: "https://thepihut.com/products/home-assistant-green",
      why: "Official plug-and-play Home Assistant box with built-in storage and power; no tinkering needed.",
    },
    tip: "Home Assistant Green is often sold out — use “Notify me”, or run Home Assistant on the Pi 5 with an SSD.",
  },
  "baby-scale": {
    value: {
      name: "Salter Add & Weigh Scale",
      shop: "Salter",
      price: "£22.99",
      link: "https://salter.com/add-weigh-bathroom-scale-for-babies-pets-luggage/",
      why: "Bathroom scale with an add-and-weigh mode: step on, zero it, pick him up and it shows just his weight.",
    },
    premium: {
      name: "Salter 2-in-1 Digital Baby & Pet Scale",
      shop: "Salter",
      price: "£29.99",
      link: "https://salter.com/products/2-in-1-digital-baby-scale.html",
      why: "Removable tray weighs up to 20 kg for the puppy months, then works as a 180 kg bathroom scale.",
    },
  },
  "canicross-harness": {
    value: {
      name: "DogFit Canicross Harness (size Small)",
      shop: "DogFit",
      price: "£37.50",
      link: "https://dogfit.co.uk/products/dogfit-canicross-harness",
      why: "UK-made padded short pulling harness. Small (neck 34-40cm, chest 55-75cm) fits an adult cocker.",
    },
    premium: {
      name: "Non-stop dogwear Freemotion 5.0 Harness",
      shop: "SnowPaw Store",
      price: "£86.95",
      link: "https://snowpawstore.com/products/freemotion-5-0-non-stop-dogwear",
      why: "Top race harness. Size 4 (14-20kg, neck 33-36cm); size 5 if lower neck is 36-40cm.",
    },
    tip: "Wait for his adult measurements, at about 12 months, before buying.",
  },
  "bungee-line": {
    value: {
      name: "DogFit Canicross Line 2m",
      shop: "DogFit",
      price: "£23.75",
      link: "https://dogfit.co.uk/products/2m-canicross-line",
      why: "UK-made 2m line with bungee section (1.5m relaxed) and a control handle by the dog clip.",
    },
    premium: {
      name: "Non-stop dogwear Bungee Leash 2m",
      shop: "SnowPaw Store",
      price: "£42.95",
      link: "https://snowpawstore.com/products/non-stop-dogwear-running-line",
      why: "Elastic along its whole length, 1.15m to 2m, with a self-locking twist carabiner. Get the 2m.",
    },
  },
  "waist-belt": {
    value: {
      name: "DogFit Canicross Belt",
      shop: "DogFit",
      price: "£39.99",
      link: "https://dogfit.co.uk/products/dogfit-canicross-belt",
      why: "Hip belt with leg straps, sliding ring and zip pocket. Standard fits up to 38in; XL is £40.99.",
    },
    premium: {
      name: "Non-stop dogwear CaniX Belt 3.0",
      shop: "SnowPaw Store",
      price: "£86.95",
      link: "https://snowpawstore.com/products/new",
      why: "Elite belt with leg loops, a sliding screw-lock carabiner and a 0.5L pocket. M or L are in stock.",
    },
  },
  "running-bottle": {
    value: {
      name: "Henry Wag Water Bottle with Leaf Bowl 500ml",
      shop: "SnowPaw Store",
      price: "£13.95",
      link: "https://snowpawstore.com/products/water-bottle-with-leaf-bowl-henry-wag",
      why: "500ml bottle (22x8cm) whose flip-out leaf becomes a bowl. Fits a vest pocket or clips on.",
    },
    premium: {
      name: "Ruffwear Trail Runner Ultralight Dog Bowl",
      shop: "Ruffwear UK",
      price: "£18.95",
      link: "https://ruffwear.co.uk/products/trail-runner-bowl",
      why: "18g, 1L waterproof fabric bowl that stuffs into its own pocket, made for running belts and vests.",
    },
  },
  "paw-protection": {
    value: {
      name: "Musher's Secret Paw Wax 60g",
      shop: "SnowPaw Store",
      price: "£14.95",
      link: "https://snowpawstore.com/products/mushers-secret",
      why: "Food-grade wax barrier for pads, and it stops snowballing. Apply 2-3 times a week. 200g is £26.",
    },
    premium: {
      name: "Ruffwear Grip Trex Dog Boots (pair)",
      shop: "SnowPaw Store",
      price: "£49.45",
      link: "https://snowpawstore.com/products/dog-boots-ruffwear-grip-trex",
      why: "Vibram-soled boots. Sold in pairs, so buy 2. Measure paw width; a cocker is often 2.00in (51mm).",
    },
    tip: "Boots are sold per pair — he needs two pairs. Measure his paws before ordering.",
  },
  "hi-vis": {
    value: {
      name: "Orbiloc Dog Dual Safety Light",
      shop: "SnowPaw Store",
      price: "£19.95",
      link: "https://snowpawstore.com/products/orbiloc-dog-dual-safety-light",
      why: "Waterproof Danish LED that clips to the harness, with 270-degree visibility, steady/flash modes and a 3-year warranty.",
    },
    premium: {
      name: "Ruffwear Lumenglow High-Vis Vest (size Small)",
      shop: "SnowPaw Store",
      price: "£59.95",
      link: "https://snowpawstore.com/products/lumenglow-high-vis-vest-ruffwear",
      why: "Blaze-orange reflective vest with a harness portal and light loops. S fits a 56-69cm chest. Ruffwear UK charges £65.95.",
    },
  },
};
